// Copyright (c) 2025 Audere, All Rights Reserved
class FileProcessorWorker {
  private worker: Worker | null = null;
  private messageId = 0;
  private pendingMessages = new Map<
    number,
    { resolve: (value: { hash: string }) => void; reject: (message?: Error) => void }
  >();

  constructor() {
    this.initWorker();
  }

  private initWorker() {
    const workerCode = `
      self.addEventListener('message', async (event) => {
        const { type, data, id } = event.data;
        try {
          switch (type) {
            case 'generateHash': {
              const { file } = data;
              const hash = await generateFileHash(file);
              self.postMessage({ type: 'hashGenerated', hash, filename: file.name, id });
              break;
            }
            case 'processFiles': {
              const { files } = data;
              const results = await processFiles(files);
              self.postMessage({ type: 'filesProcessed', results, id });
              break;
            }
            default:
              self.postMessage({ type: 'error', error: 'Unknown message type', id });
          }
        } catch (error) {
          self.postMessage({ type: 'error', error: error.message, id });
        }
      });

      const arrayBufferToHex = (buffer) => {
        return Array.from(new Uint8Array(buffer))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');
      };
      
      const generateFileHash = async (file) => {
        const arrayBuffer = await file.arrayBuffer();
        const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
        return arrayBufferToHex(hashBuffer);
      };
      
      const processFiles = async (files) => {
        const results = [];
        for (const file of files) {
          const hash = await generateFileHash(file);
          results.push({
            hash,
            filename: file.name,
            size: file.size
          });
        }
        return results;
      };
    `;

    const blob = new Blob([workerCode], { type: "application/javascript" });
    this.worker = new Worker(URL.createObjectURL(blob));

    this.worker.onmessage = event => {
      const { id, type, ...data } = event.data;
      const pending = this.pendingMessages.get(id);

      if (pending) {
        this.pendingMessages.delete(id);
        if (type === "error") {
          pending.reject(new Error(data.error));
        } else {
          pending.resolve(data);
        }
      }
    };
  }

  private sendMessage(type: string, data: { file: File }): Promise<{ hash: string }> {
    return new Promise((resolve, reject) => {
      if (!this.worker) {
        reject(new Error("Worker not initialized"));
        return;
      }

      const id = ++this.messageId;
      this.pendingMessages.set(id, { resolve, reject });

      this.worker.postMessage({ type, data, id });
    });
  }

  async generateHash(file: File): Promise<string> {
    const result = await this.sendMessage("generateHash", { file });
    return result.hash;
  }

  async processFiles(
    files: File[],
    onProgress?: (result: { hash: string; filename: string; size: number }) => void
  ): Promise<Array<{ hash: string; filename: string; size: number }>> {
    const results = [];

    for (const file of files) {
      const hash = await this.generateHash(file);
      const result = { hash, filename: file.name, size: file.size };
      results.push(result);
      if (onProgress) {
        onProgress(result);
      }
    }

    return results;
  }

  terminate() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }
}

let workerInstance: FileProcessorWorker | null = null;

export const getFileProcessorWorker = (): FileProcessorWorker => {
  if (!workerInstance) {
    workerInstance = new FileProcessorWorker();
  }
  return workerInstance;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};
